import { FC, useState } from "react";
import { FileViewer, styled } from "@monolith-forensics/monolith-ui";
import { FileIcon } from "lucide-react";

import Loader from "@/components/Loader";
import { convertBytesToSize } from "@/utils/convertBytesToSize";
import { monolithMoment } from "@/utils/date-format";
import type { AssetAttachment } from "@/Features/AssetAttachments";
import {
  FileList,
  FileRow,
  useValidationReports,
} from "@/Features/AssetAttachments";

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0px;
`;

const SectionHeader = styled.h3`
  margin: 0px;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

/**
 * The reports attached to one validation (ENG-1673), read-only. Each row
 * opens the file in mfui's FileViewer. `file_url` is passed through as
 * signed (no localhost:3000 → 3001 rewrite), the same as Case Files and
 * Evidence Photos do. The parent keys this on the validation uuid so the
 * viewer selection resets on Next/Previous.
 */
export const ValidationReports: FC<{ validationUuid: string }> = ({
  validationUuid,
}) => {
  const { data, isLoading, isError } = useValidationReports(validationUuid);
  const [selected, setSelected] = useState<AssetAttachment | null>(null);

  return (
    <Section aria-label="Reports">
      <SectionHeader>Reports</SectionHeader>
      {isLoading ? (
        <Loader />
      ) : isError && !data ? (
        <Muted>Couldn't load reports.</Muted>
      ) : !data?.length ? (
        <Muted>No reports</Muted>
      ) : (
        <FileList>
          {data.map((row) => (
            <li key={row.uuid}>
              <FileRow
                type="button"
                disabled={!row.file_url}
                onClick={() => setSelected(row)}
              >
                <FileIcon size={14} />
                <span className="file-name" title={row.name ?? undefined}>
                  {row.name ?? row.uuid}
                </span>
                {row.size != null && (
                  <span className="file-meta">
                    {convertBytesToSize(row.size)}
                  </span>
                )}
                {row.created_on && (
                  <span className="file-meta">
                    {monolithMoment({ timestamp: row.created_on })}
                  </span>
                )}
              </FileRow>
            </li>
          ))}
        </FileList>
      )}
      <FileViewer
        file={{
          name: selected?.name ?? selected?.uuid ?? "",
          url: selected?.file_url ?? undefined,
          ext: selected?.ext ?? undefined,
          size: selected?.size ?? undefined,
        }}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </Section>
  );
};
